import { BookedService } from "../types/transactions";
import { ClientServicePackage } from "../types/packages";
import { ServicePriceBasis } from "../types/context";
import { formatQty, isPerUnitBasis, unitLabelForBasis } from "./quantityUtils";
import { calculateDiscountedItemPrice, hasLineDiscount } from "./discountUtils";

/** Minimal salon-service shape needed to label package balances by unit. */
export interface ServiceBasisRef {
  id: string;
  price_basis?: ServicePriceBasis | string | null;
}

/** A booked-service line that a given package can cover, with its balance. */
export interface CoveredLine {
  bookedServiceId: string;
  salonServiceId: string;
  serviceName: string;
  quantityRemaining: number;
  /** Units/syringes recorded on the line item (what was actually used). */
  usedQuantity: number;
  /** Units already redeemed against this line across ALL packages. */
  unitsRedeemed: number;
  /**
   * Units on this line still available to redeem (usedQuantity − unitsRedeemed).
   * Two packages can cover one line iteratively: after redeeming from package A
   * the transaction refetches with a reduced remaining, so package B is capped
   * at the leftover. Never negative.
   */
  unitsRemaining: number;
}

/**
 * Packages that cover at least one service on the transaction and still have
 * remaining balance for it. Single source for the checkout Package method
 * gate and the details-screen heads-up note.
 */
export function filterRedeemablePackages(
  clientPackages: ClientServicePackage[],
  bookedServices: BookedService[],
): ClientServicePackage[] {
  const txnServiceIds = new Set(
    bookedServices.map((bs) => bs.salon_service_id),
  );
  return clientPackages.filter((pkg) =>
    pkg.services.some(
      (svc) =>
        svc.quantity_remaining > 0 && txnServiceIds.has(svc.salon_service_id),
    ),
  );
}

/**
 * Whether a package still has any redeemable balance. The backend already
 * excludes depleted/expired packages; this is a front-end guard so a
 * fully-drawn-down package never renders as a selectable (but useless) row.
 */
export function hasRemainingBalance(pkg: ClientServicePackage): boolean {
  return pkg.services.some((svc) => svc.quantity_remaining > 0);
}

/** Booked-service lines covered by the selected package, with balances. */
export function buildCoveredLines(
  selectedPackage: ClientServicePackage | undefined,
  bookedServices: BookedService[],
): CoveredLine[] {
  if (!selectedPackage) return [];
  const balanceByServiceId = new Map(
    selectedPackage.services.map((svc) => [
      svc.salon_service_id,
      svc.quantity_remaining,
    ]),
  );
  return bookedServices
    .filter((bs) => (balanceByServiceId.get(bs.salon_service_id) ?? 0) > 0)
    .map((bs) => {
      const usedQuantity = bs.quantity ?? 1;
      const unitsRedeemed = bs.calc__units_redeemed ?? 0;
      return {
        bookedServiceId: bs.id,
        salonServiceId: bs.salon_service_id,
        serviceName: bs.name,
        quantityRemaining: balanceByServiceId.get(bs.salon_service_id) ?? 0,
        usedQuantity,
        unitsRedeemed,
        unitsRemaining: Math.max(0, usedQuantity - unitsRedeemed),
      };
    });
}

/**
 * The most a covered line may redeem right now: the smaller of the package's
 * remaining balance and the units still unredeemed on the line item. Never
 * below 0. The backend enforces the same cap as a safety net.
 */
export function maxRedeemableForLine(line: CoveredLine): number {
  return Math.max(0, Math.min(line.quantityRemaining, line.unitsRemaining));
}

/**
 * Default units to redeem for a covered line: the units still unredeemed on
 * the line item, but never more than the package's remaining balance (if more
 * were used than the package holds, redeem all of it and the rest is charged).
 */
export function defaultRedeemQuantity(line: CoveredLine): number {
  return maxRedeemableForLine(line);
}

/**
 * The dollar value a package covered on a booked-service line, excluding
 * tax/tip/discount (units × list unit price). Prefers the backend-computed
 * `calc__amount_redeemed`; falls back to (calc__cost − calc__cost_after_
 * redemptions) for stale payloads that predate that field. 0 when nothing is
 * redeemed.
 */
export function redeemedAmountForService(service: BookedService): number {
  const unitsRedeemed = service.calc__units_redeemed ?? 0;
  if (unitsRedeemed <= 0) return 0;
  if (service.calc__amount_redeemed != null) {
    return Math.max(0, service.calc__amount_redeemed);
  }
  const cost = service.calc__cost ?? 0;
  const net = service.calc__cost_after_redemptions ?? cost;
  return Math.max(0, cost - net);
}

/**
 * A line's net cost after BOTH discount and package redemption, excluding
 * tax/tip: the discounted line price (excl tax/tip) minus the redeemed value,
 * floored at 0. This is the "net" figure shown per line in the modal.
 */
export function netCostAfterRedemptionExclTaxTip(
  service: BookedService,
): number {
  const discountedExclTaxTip = hasLineDiscount(service)
    ? calculateDiscountedItemPrice(service)
    : service.calc__cost_excl_tips_discounts;
  return Math.max(0, discountedExclTaxTip - redeemedAmountForService(service));
}

export interface RemainingLinePricing {
  /** Units still billed to the client (line quantity − units redeemed). */
  remainingUnits: number;
  /** Remaining units' full price (pre-discount), excl tax/tip. */
  full: number;
  /** Remaining units' price after the line discount, excl tax/tip. */
  discounted: number;
  /** True when the line carries a discount (drives the strikethrough). */
  hasDiscount: boolean;
}

/**
 * Prices the portion of a service line the client still pays for after package
 * redemptions: the unredeemed fraction of the line, at both full (pre-discount)
 * and discounted prices, excl tax/tip. Redeemed units are covered by the
 * package and drop out. Scaling the line's own `calc__cost_excl_tips_discounts`
 * by the remaining fraction (rather than `unitPrice × remaining`) keeps modifier
 * pricing intact and makes an un-redeemed line render exactly as before
 * (fraction = 1). For a plain per-unit line this equals remaining × unit price.
 */
export function remainingLinePricing(
  service: BookedService,
): RemainingLinePricing {
  const totalUnits = service.quantity ?? 1;
  const remainingUnits = Math.max(
    0,
    totalUnits - (service.calc__units_redeemed ?? 0),
  );
  const fraction = totalUnits > 0 ? remainingUnits / totalUnits : 0;
  return {
    remainingUnits,
    full: (service.calc__cost_excl_tips_discounts ?? 0) * fraction,
    discounted: calculateDiscountedItemPrice(service) * fraction,
    hasDiscount: hasLineDiscount(service),
  };
}

/**
 * Per-line label so two packages can cover one service without the operator
 * doing mental math: "Facial — 1 of 3 redeemed, 2 left (3 in package)".
 */
export function coveredLineLabel(line: CoveredLine): string {
  return (
    `${line.serviceName} — ${formatQty(line.unitsRedeemed)} of ` +
    `${formatQty(line.usedQuantity)} redeemed, ` +
    `${formatQty(line.unitsRemaining)} left ` +
    `(${formatQty(line.quantityRemaining)} in package)`
  );
}

/** "1/3 Hydrafacials"-style remaining chip label for a package balance. */
export function remainingChipLabel(svc: {
  service_name: string;
  quantity_total: number;
  quantity_remaining: number;
}): string {
  return `${formatQty(svc.quantity_remaining)}/${formatQty(
    svc.quantity_total,
  )} ${svc.service_name}`;
}

/** Does this package cover at least one redeemable service on the transaction? */
export function isPackageApplicable(
  pkg: ClientServicePackage,
  bookedServices: BookedService[],
): boolean {
  return filterRedeemablePackages([pkg], bookedServices).length > 0;
}

/**
 * Package units still available to a covered line after accounting for what
 * SIBLING lines of the same service are already redeeming — two ticket lines
 * of one service share a single package balance, so their entered amounts
 * must not jointly exceed it.
 */
function packageHeadroomForLine(
  line: CoveredLine,
  redemptions: Record<string, number>,
  coveredLines: CoveredLine[],
): number {
  const siblingsTotal = coveredLines
    .filter(
      (other) =>
        other.salonServiceId === line.salonServiceId &&
        other.bookedServiceId !== line.bookedServiceId,
    )
    .reduce(
      (sum, other) => sum + (Number(redemptions[other.bookedServiceId]) || 0),
      0,
    );
  return Math.max(0, line.quantityRemaining - siblingsTotal);
}

/**
 * The most a covered line may redeem right now given current sibling entries:
 * min(units still unredeemed on the line, remaining package headroom). Reduces
 * to `maxRedeemableForLine(line)` when the service appears once on the ticket.
 */
export function sharedBalanceAwareMax(
  line: CoveredLine,
  redemptions: Record<string, number>,
  coveredLines: CoveredLine[],
): number {
  const headroom = packageHeadroomForLine(line, redemptions, coveredLines);
  return Math.max(0, Math.min(line.unitsRemaining, headroom));
}

/**
 * Why a line's "+" is disabled at its cap — names whether the package balance
 * or the ticket quantity is the binding limit, so staff know which side to fix.
 */
export function capReasonTooltip(
  line: CoveredLine,
  redemptions: Record<string, number>,
  coveredLines: CoveredLine[],
): string {
  const headroom = packageHeadroomForLine(line, redemptions, coveredLines);
  if (headroom <= line.unitsRemaining) {
    return `Only ${formatQty(headroom)} of ${line.serviceName} left on this package.`;
  }
  return `You can't redeem more ${line.serviceName} than the ${formatQty(
    line.unitsRemaining,
  )} on this ticket.`;
}

/**
 * The info-tooltip lines for a package card: every banked service that still
 * has a remaining balance (including services not on this ticket). Per-unit /
 * per-syringe services read "20 units of Botox"; count-based services read
 * "1 of 3 Hydrafacial".
 */
export function remainingBalanceTooltipItems(
  pkg: ClientServicePackage,
  salonServices: ServiceBasisRef[],
): string[] {
  const basisById = new Map(
    salonServices.map((svc) => [svc.id, svc.price_basis]),
  );
  return pkg.services
    .filter((svc) => svc.quantity_remaining > 0)
    .map((svc) => {
      const basis = basisById.get(svc.salon_service_id);
      if (isPerUnitBasis(basis)) {
        const unit = unitLabelForBasis(basis, svc.quantity_remaining);
        return `${formatQty(svc.quantity_remaining)} ${unit} of ${
          svc.service_name
        }`;
      }
      return `${formatQty(svc.quantity_remaining)} of ${formatQty(
        svc.quantity_total,
      )} ${svc.service_name}`;
    });
}
